import { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Search, X, ChevronLeft, ChevronRight,
  FileText, FileSpreadsheet, Download, Columns,
  UserCheck, ArrowRightLeft, GitMerge, ChevronDown,
  MoreVertical, Eye, Pencil, MessageSquarePlus, History, Trash2,
  SlidersHorizontal, Bookmark, ArrowUpDown, Upload, LayoutList, LayoutGrid,
  MessageSquare, MessagesSquare,
 Inbox, FilterX, Copy, User as UserIcon, Link2, Loader2 } from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { DateInput } from '@/components/ui/DateInput';
import { Button } from '@/components/ui/button';
import api from '@/lib/api';
import { toast } from '@/components/ui/toast';
import { LeadFormModal } from '@/components/leads/LeadFormModal';
import { FollowupDrawer } from '@/components/leads/FollowupDrawer';
import { LeadLogsModal } from '@/components/leads/LeadLogsModal';
import { SendMessageModal, type MessageChannel } from '@/components/leads/SendMessageModal';
import { UpgradePlanModal } from '@/components/UpgradePlanModal';
import { CsvImportModal } from '@/components/leads/CsvImportModal';
import { ConfirmModal } from '@/components/ConfirmModal';
import { KanbanBoard } from '@/components/leads/KanbanBoard';
import { DuplicateWarningModal } from '@/components/leads/DuplicateWarningModal';
import { ScoreBadge } from '@/components/leads/ScoreBadge';
import { TableRowSkeleton, CardRowSkeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/EmptyState';
import { getCountryNames, getStateNames, getCityNames, getCountries, countryIdByName, stateIdByName, cityIdByName } from '@/lib/geoApi';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import { useAuth } from '@/contexts/AuthContext';
import {
  ALL_LEAD_COLUMNS, FACTORY_DEFAULT_COLUMNS,
  loadUserColumns, saveUserColumns, loadStoredVendorHash, vendorHash,
} from '@/lib/leadColumns';
import { formatDate, formatDateTime } from '@/lib/dateFormat';
import { LeadDetailDrawer } from '@/components/leads/LeadDetailDrawer';
import { formatLeadPhone } from '@/components/ui/PhoneInput';
import { useSort, SortTh, TableHeadRow } from '@/components/ui/sortable-table';
import { Floating } from '@/components/ui/Floating';
import { SelectField } from '@/components/ui/SelectField';

interface Lead {
  id: string;
  leadNo?: string | null;
  name: string;
  firstName?: string;
  lastName?: string;
  phones: any[];
  emails: any[];
  source: string | null;
  status: string;
  priority: string | null;
  score: number;
  city: string | null;
  country: string | null;
  state: string | null;
  pincode: string | null;
  notes: string | null;
  campaignName: string | null;
  leadCategory: string | null;
  leadColor: string | null;
  hashtags: any[];
  createdAt: string;
  assignedTo: { id: string; name: string } | null;
}


const priorityColors: Record<string, string> = {
  high: 'bg-red-50 text-red-600 border border-red-200',
  medium: 'bg-amber-50 text-amber-600 border border-amber-200',
  low: 'bg-teal-50 text-teal-600 border border-teal-200',
  hot: 'bg-red-50 text-red-600 border border-red-200',
  warm: 'bg-amber-50 text-amber-600 border border-amber-200',
  cold: 'bg-teal-50 text-teal-600 border border-teal-200',
};

function getPriorityLabel(priority: string | null): string {
  if (!priority) return '';
  const map: Record<string, string> = { hot: 'High', warm: 'Medium', cold: 'Low' };
  return map[priority.toLowerCase()] ?? priority;
}

const SORT_LABELS: Record<string, string> = {
  name: 'Name', createdAt: 'Created', updatedAt: 'Updated', status: 'Status',
  priority: 'Priority', source: 'Source', country: 'Country', city: 'City',
};

// "14:30" → "02:30 PM"
const fmtTime12 = (hhmm?: string | null) => {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  if (Number.isNaN(h)) return hhmm;
  return `${String(h % 12 || 12).padStart(2, '0')}:${String(m || 0).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
};

export function LeadsPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [viewMode, setViewMode] = useState<'table' | 'kanban'>('table');
  const [statuses, setStatuses] = useState<any[]>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showColumnsMenu, setShowColumnsMenu] = useState(false);
  const columnsBtnRef = useRef<HTMLButtonElement>(null);
  const columnsDropdownRef = useRef<HTMLDivElement>(null);
  const sortWrapperRef = useRef<HTMLDivElement>(null);
  const [columnsMenuPos, setColumnsMenuPos] = useState({ top: 0, right: 0 });

  // Column state
  const [visibleColumns, setVisibleColumns] = useState<string[]>(FACTORY_DEFAULT_COLUMNS);
  const [vendorDefaults, setVendorDefaults] = useState<string[]>(FACTORY_DEFAULT_COLUMNS);
  const [showVendorUpdateBanner, setShowVendorUpdateBanner] = useState(false);
  const [columnsReady, setColumnsReady] = useState(false);

  const [statusOptions, setStatusOptions] = useState<{id: string; name: string; color: string}[]>([]);
  const [sourceOptions, setSourceOptions] = useState<string[]>([]);
  const [actionMenu, setActionMenu] = useState<{ leadId: string; rect: DOMRect } | null>(null);
  // Per-lead booking links: the assigned counselor's active events + a
  // signed ref that prefills the booking form and links it to the lead.
  type BookingLinks = { counselorId: string; ref: string; events: { id: string; name: string; slug: string; shortCode?: string | null }[] };
  const [bookingLinks, setBookingLinks] = useState<Record<string, BookingLinks | 'loading' | null>>({});
  // Lead opened in the right-side detail drawer (click on the lead no).
  const [drawerLeadId, setDrawerLeadId] = useState<string | null>(null);
  // Status-cell remarks expanded via "more" (lead ids).
  const [expandedRemarks, setExpandedRemarks] = useState<Set<string>>(new Set());
  const toggleRemark = (leadId: string) =>
    setExpandedRemarks((prev) => {
      const next = new Set(prev);
      if (next.has(leadId)) next.delete(leadId); else next.add(leadId);
      return next;
    });
  const [followupLead, setFollowupLead] = useState<any>(null);
  const [logsLeadId, setLogsLeadId] = useState<string | null>(null);
  // Outbound message modal state. One source of truth for both SMS + WhatsApp
  // because the SendMessageModal switches its endpoint + channel via a prop.
  const [messageTarget, setMessageTarget] = useState<{ channel: MessageChannel; leadId: string; leadName: string; phone: string } | null>(null);
  const [editLead, setEditLead] = useState<any>(null);
  const [upgradeModal, setUpgradeModal] = useState<string | null>(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showMoveDrawer, setShowMoveDrawer] = useState(false);
  const [showMergeDrawer, setShowMergeDrawer] = useState(false);
  // Replaces the native window.confirm() on the row-level Delete action so
  // the confirmation matches the styled ConfirmModal used everywhere else
  // (Trash page, Roles, Teams, etc.). Stores the lead being deleted so the
  // modal can name it; null when hidden.
  const [confirmDeleteLead, setConfirmDeleteLead] = useState<{ id: string; name: string } | null>(null);
  const [confirmDeleteLoading, setConfirmDeleteLoading] = useState(false);

  const [filters, setFilters] = useState({
    search: '', status: '', source: '', dateFrom: '', dateTo: '',
    priority: '', country: '', state: '', city: '', pincode: '',
    campaignName: '', leadCategory: '', assignedToId: '',
    name: '', phone: '', email: '', notes: '',
    hashtag: '', utmSource: '', utmMedium: '', utmCampaign: '',
    utmTerm: '', utmContent: '', updatedFrom: '', updatedTo: '',
    page: 1,
  });
  const [showFilters, setShowFilters] = useState(false);
  const [savedFilters, setSavedFilters] = useState<{id: string; name: string; filters: any}[]>([]);
  const [saveFilterName, setSaveFilterName] = useState('');
  const [showSaveInput, setShowSaveInput] = useState(false);
  const [users, setUsers] = useState<{id: string; name: string}[]>([]);
  const [filterSearch, setFilterSearch] = useState('');
  const [financialYears, setFinancialYears] = useState<{id: string; name: string}[]>([]);
  const [activeFYId, setActiveFYId] = useState<string>('');
  const [filterSection, setFilterSection] = useState({ saved: true, basic: true, location: false, dates: false, campaign: false, utm: false });
  const [showSort, setShowSort] = useState(false);
  const [showExport, setShowExport] = useState(false);
  const exportWrapperRef = useRef<HTMLDivElement>(null);
  const [sortField, setSortField] = useState('');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  // Load vendor defaults + user column prefs on mount.
  // Tenant settings are cached in sessionStorage since they change rarely.
  useEffect(() => {
    if (!user) return;
    const TENANT_CACHE = 'leads:tenant-me';

    const applyTenantData = (tenant: any) => {
      const settings = (tenant?.settings as any) || {};
      // Lead No became its own column; older saved vendor defaults lack it.
      const savedDefaults: string[] | undefined = settings.leadDefaultColumns;
      const vDefaults: string[] = savedDefaults
        ? [
            ...(savedDefaults.includes('leadNo') ? [] : ['leadNo']),
            ...savedDefaults,
            ...(savedDefaults.includes('followup') ? [] : ['followup']),
          ]
        : FACTORY_DEFAULT_COLUMNS;
      setVendorDefaults(vDefaults);
      const vHash = vendorHash(vDefaults);

      let stored = loadUserColumns(user.id);
      // One-time: show the new Meeting column to people with saved prefs.
      try {
        if (stored && !stored.includes('meeting') && !localStorage.getItem('lead_columns_meeting_v1')) {
          stored = [...stored, 'meeting'];
          saveUserColumns(user.id, stored, loadStoredVendorHash(user.id) ?? vHash);
        }
        localStorage.setItem('lead_columns_meeting_v1', '1');
        // One-time: Lead No split out of the name column.
        if (stored && !stored.includes('leadNo') && !localStorage.getItem('lead_columns_leadno_v1')) {
          stored = ['leadNo', ...stored];
          // Our own default change, not the admin's: don't show the
          // "admin updated the default columns" banner for it.
          saveUserColumns(user.id, stored, vHash);
        }
        localStorage.setItem('lead_columns_leadno_v1', '1');
        // One-time: Follow-up split out of the Status column.
        if (stored && !stored.includes('followup') && !localStorage.getItem('lead_columns_followup_v1')) {
          stored = [...stored, 'followup'];
          saveUserColumns(user.id, stored, vHash);
        }
        localStorage.setItem('lead_columns_followup_v1', '1');
      } catch {}
      if (stored) {
        setVisibleColumns(stored);
        const prevHash = loadStoredVendorHash(user.id);
        if (prevHash && prevHash !== vHash) setShowVendorUpdateBanner(true);
      } else {
        setVisibleColumns(vDefaults);
        saveUserColumns(user.id, vDefaults, vHash);
      }
    };

    // Hydrate from cache first
    try {
      const cached = sessionStorage.getItem(TENANT_CACHE);
      if (cached) {
        applyTenantData(JSON.parse(cached));
        setColumnsReady(true);
      }
    } catch {}

    // Then refresh in background
    api.get('/tenant/me').then(r => {
      applyTenantData(r.data);
      try { sessionStorage.setItem(TENANT_CACHE, JSON.stringify(r.data)); } catch {}
    }).catch(() => {
      setVisibleColumns(FACTORY_DEFAULT_COLUMNS);
      setVendorDefaults(FACTORY_DEFAULT_COLUMNS);
    }).finally(() => setColumnsReady(true));
  }, [user]);

  // ── Consolidated metadata fetch ─────────────────────────────────────
  // Previously this page fired 4 separate useEffects on mount, each paying
  // full JWT auth + DB latency. And /leads/statuses was duplicated
  // (another useEffect lower down). On slow links that was 1-2 seconds
  // before the page felt usable.
  //
  // Now: all four requests in a single Promise.all, each sessionStorage-
  // cached since these values are near-static during a session.
  useEffect(() => {
    type Cached<T> = { t: number; data: T };
    const TTL_MS = 5 * 60 * 1000; // 5 min — these lists rarely change
    const now = Date.now();

    const readCache = <T,>(key: string): T | null => {
      try {
        const raw = sessionStorage.getItem(key);
        if (!raw) return null;
        const parsed: Cached<T> = JSON.parse(raw);
        if (now - parsed.t > TTL_MS) return null;
        return parsed.data;
      } catch { return null; }
    };
    const writeCache = <T,>(key: string, data: T) => {
      try { sessionStorage.setItem(key, JSON.stringify({ t: now, data })); } catch {}
    };

    // Hydrate immediately from cache so the UI renders something fast.
    const cs = readCache<any[]>('leads:statuses');
    if (cs) { setStatusOptions(cs); setStatuses(cs); }
    const csf = readCache<any[]>('leads:saved-filters');
    if (csf) setSavedFilters(csf);
    const cu = readCache<any[]>('leads:assignable');
    if (cu) setUsers(cu);
    const cfy = readCache<any[]>('leads:financial-years');
    if (cfy) {
      setFinancialYears(cfy.map((fy: any) => ({ id: fy.id, name: fy.name })));
      const active = cfy.find((fy: any) => fy.isActive);
      if (active) setActiveFYId(active.id);
    }

    // Then refresh in background. One Promise.all — backend can fan these
    // out in parallel instead of waiting serially as the user scrolls.
    Promise.all([
      api.get('/leads/statuses').then((r) => r.data).catch(() => null),
      api.get('/leads/saved-filters').then((r) => r.data).catch(() => null),
      // /users/assignable returns role-scoped + admin-excluded options
      // for the Assignee filter (manager → team, counselor → self).
      api.get('/users/assignable').then((r) => r.data).catch(() => null),
      api.get('/tenant/financial-years').then((r) => r.data).catch(() => null),
      // Source master for the Source filter (includes auto-created sources
      // like "Web Form" — no longer a hardcoded list).
      api.get('/leads/sources').then((r) => r.data).catch(() => null),
    ]).then(([statuses, savedFs, usersData, fys, sources]) => {
      if (statuses) {
        setStatusOptions(statuses);
        setStatuses(statuses); // same data — keep both state slots in sync
        writeCache('leads:statuses', statuses);
      }
      if (Array.isArray(sources)) {
        setSourceOptions(sources.map((s: any) => s?.name ?? s).filter(Boolean));
      }
      if (savedFs) {
        setSavedFilters(savedFs);
        writeCache('leads:saved-filters', savedFs);
      }
      if (usersData) {
        const list = Array.isArray(usersData)
          ? usersData
          : (usersData as any)?.users ?? (usersData as any)?.data ?? [];
        const mapped = list.map((u: any) => ({ id: u.id, name: u.name }));
        setUsers(mapped);
        writeCache('leads:assignable', list);
      }
      if (fys && Array.isArray(fys)) {
        setFinancialYears(fys.map((fy: any) => ({ id: fy.id, name: fy.name })));
        const active = fys.find((fy: any) => fy.isActive);
        if (active) setActiveFYId(active.id);
        writeCache('leads:financial-years', fys);
      }
    });
  }, []);

  // Load the booking links for the lead whose menu just opened (once per lead).
  useEffect(() => {
    if (!actionMenu) return;
    const id = actionMenu.leadId;
    const lead: any = leads.find((l: any) => l.id === id);
    if (!lead?.assignedTo?.id || bookingLinks[id] !== undefined) return;
    setBookingLinks(m => ({ ...m, [id]: 'loading' }));
    api.get(`/leads/${id}/meetings/bookable-events`)
      .then(({ data }) => setBookingLinks(m => ({
        ...m,
        [id]: data?.assignedTo?.id && data?.ref && data.events?.length
          ? { counselorId: data.assignedTo.id, ref: data.ref, events: data.events }
          : null,
      })))
      .catch(() => setBookingLinks(m => ({ ...m, [id]: null })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [actionMenu]);

  const copyBookingLink = (links: BookingLinks, ev: BookingLinks['events'][number]) => {
    // Short link when the server issued a code; long signed link as fallback.
    const url = ev.shortCode
      ? `${window.location.origin}/m/${ev.shortCode}`
      : `${window.location.origin}/book/${links.counselorId}/${ev.slug}?ref=${encodeURIComponent(links.ref)}`;
    const eventName = ev.name;
    navigator.clipboard?.writeText(url)
      .then(() => toast('success', `${eventName} link copied`))
      .catch(() => toast('error', 'Could not copy the link'));
  };

  // Close action menu on outside click
  useEffect(() => {
    const handler = () => setActionMenu(null);
    if (actionMenu) document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [actionMenu]);

  // Preload the country master so country filters resolve to master ids.
  useEffect(() => { getCountries().catch(() => {}); }, []);

  // Close columns menu on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        columnsBtnRef.current?.contains(target) ||
        columnsDropdownRef.current?.contains(target)
      ) return;
      setShowColumnsMenu(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const openColumnsMenu = () => {
    if (!showColumnsMenu && columnsBtnRef.current) {
      const rect = columnsBtnRef.current.getBoundingClientRect();
      setColumnsMenuPos({ top: rect.bottom + 6, right: window.innerWidth - rect.right });
    }
    setShowColumnsMenu(v => !v);
  };

  const updateColumns = (cols: string[]) => {
    setVisibleColumns(cols);
    if (user) saveUserColumns(user.id, cols, vendorHash(vendorDefaults));
  };

  const toggleColumn = (key: string) => {
    updateColumns(
      visibleColumns.includes(key)
        ? visibleColumns.filter(k => k !== key)
        : [...visibleColumns, key],
    );
  };

  const selectAllColumns = () => updateColumns(ALL_LEAD_COLUMNS.map(c => c.key));

  const resetToDefault = () => {
    updateColumns(vendorDefaults);
    setShowVendorUpdateBanner(false);
  };

  const dismissVendorBanner = () => {
    if (user) saveUserColumns(user.id, visibleColumns, vendorHash(vendorDefaults));
    setShowVendorUpdateBanner(false);
  };

  const vis = (key: string) => visibleColumns.includes(key);

  const copyLeadNo = (lead: any) => {
    const no = lead.leadNo ? `#${lead.leadNo}` : `#${lead.id.slice(-6).toUpperCase()}`;
    navigator.clipboard?.writeText(no).then(() => toast('success', `${no} copied`)).catch(() => {});
  };

  // Latest meeting / demo: just its date and time.
  const renderMeeting = (lead: any) => {
    const b = Array.isArray(lead.bookings) ? lead.bookings[0] : null;
    if (!b) return <span className="text-sm text-slate-400">—</span>;
    return <span className="text-sm text-slate-600 whitespace-nowrap">{formatDate(b.date)} {fmtTime12(b.startTime)}</span>;
  };

  // Latest follow-up: "who · when" + remark with more/less.
  // Shared by the desktop table and the mobile cards.
  const renderLatestFollowup = (lead: any) => {
    const fl = lead.followups;
    const f = Array.isArray(fl) ? fl[0] : null;
    if (!f) return null;
    const remark: string = f.remark || '';
    const long = remark.length > 90;
    const open = expandedRemarks.has(lead.id);
    return (
      <div>
        <p className="text-xs text-slate-500">
          {f.user?.name ? `${f.user.name} · ` : ''}{formatDateTime(f.createdAt)}
        </p>
        {remark && (
          <p className="mt-0.5 text-xs text-slate-700 whitespace-pre-line break-words">
            {long && !open ? `${remark.slice(0, 90).trimEnd()}…` : remark}
            {long && (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); toggleRemark(lead.id); }}
                className="ml-1 font-medium text-[#6366F1] hover:underline"
              >
                {open ? 'less' : 'more'}
              </button>
            )}
          </p>
        )}
      </div>
    );
  };

  const activeFilterCount = Object.entries(filters).filter(([k, v]) => k !== 'page' && k !== 'search' && v).length;

  const fetchLeads = useCallback(async (f = filters) => {
    setIsLoading(true);
    try {
      const params: any = { page: f.page, limit: 25 };
      Object.entries(f).forEach(([k, v]) => {
        if (k !== 'page' && v) params[k] = k === 'status' ? String(v).toLowerCase() : v;
      });
      // Send master geo ids when the typed filter matches a master entry; the
      // backend falls back to name matching (via the join) otherwise.
      const cid = countryIdByName(f.country);
      const sid = stateIdByName(f.country, f.state);
      const ciid = cityIdByName(f.country, f.state, f.city);
      if (cid) { params.countryId = cid; delete params.country; }
      if (sid) { params.stateId = sid; delete params.state; }
      if (ciid) { params.cityId = ciid; delete params.city; }
      if (sortField) { params.sortBy = sortField; params.sortOrder = sortOrder; }
      if (activeFYId) params.financialYearId = activeFYId;
      // POST so the full filter set (incl. PII like search / name / phone /
      // email) travels in the body, never in the URL / logs / history.
      const { data } = await api.post('/leads/search', params);
      setLeads(data.leads);
      setTotal(data.total);
      setTotalPages(data.totalPages);
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to load leads');
    }
    finally { setIsLoading(false); }
  }, [filters]);

  // Debounce text-search inputs so typing "John" doesn't fire 4 queries.
  // Non-text filters (status dropdown, date pickers) are left un-debounced
  // because they change in discrete clicks and users expect immediate feedback.
  useEffect(() => {
    const timer = setTimeout(() => { fetchLeads(filters); }, 300);
    return () => clearTimeout(timer);
  }, [filters, activeFYId]);
  // Note: the duplicate /leads/statuses call that used to live here has
  // been removed. Statuses are now fetched once in the consolidated
  // metadata Promise.all above and written into BOTH statusOptions and
  // statuses state slots.

  const clearFilters = () => {
    setFilters({
      search: '', status: '', source: '', dateFrom: '', dateTo: '',
      priority: '', country: '', state: '', city: '', pincode: '',
      campaignName: '', leadCategory: '', assignedToId: '',
      name: '', phone: '', email: '', notes: '',
      hashtag: '', utmSource: '', utmMedium: '', utmCampaign: '',
      utmTerm: '', utmContent: '', updatedFrom: '', updatedTo: '',
      page: 1,
    });
  };

  const handleSaveFilter = async () => {
    if (!saveFilterName.trim()) return;
    const { page, ...filterData } = filters;
    try {
      const { data } = await api.post('/leads/saved-filters', { name: saveFilterName, filters: filterData });
      setSavedFilters(prev => [data, ...prev]);
      setSaveFilterName('');
      setShowSaveInput(false);
      toast('success', 'Filter saved');
    } catch { toast('error', 'Failed to save filter'); }
  };

  const handleApplySavedFilter = (savedFilter: any) => {
    setFilters({ ...savedFilter.filters, page: 1 });
  };

  const handleDeleteSavedFilter = async (id: string) => {
    try {
      await api.delete(`/leads/saved-filters/${id}`);
      setSavedFilters(prev => prev.filter(f => f.id !== id));
      toast('success', 'Filter deleted');
    } catch { toast('error', 'Failed to delete'); }
  };

  const allSelected = leads.length > 0 && leads.every(l => selectedIds.has(l.id));
  const someSelected = selectedIds.size > 0;

  const toggleSelectAll = () => {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(leads.map(l => l.id)));
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const pageOffset = (filters.page - 1) * 25;

  // Client-side sort of the visible page for the desktop table header.
  // Named distinctly so it doesn't collide with the server-side sortField/sortOrder.
  const { sorted: sortedLeads, sort: tableSort, onSort: onTableSort } = useSort(leads);

  const getExportParams = () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => {
      if (k !== 'page' && v) params.set(k, k === 'status' ? String(v).toLowerCase() : String(v));
    });
    return params;
  };

  const handleCsvExport = async () => {
    try {
      const { data } = await api.get(`/leads/export?${getExportParams()}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([data], { type: 'text/csv' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'leads.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch { toast('error', 'Failed to export CSV'); }
  };

  const handleExcelExport = async () => {
    try {
      const { data } = await api.get(`/leads/export?${getExportParams()}`, { responseType: 'blob' });
      const url = URL.createObjectURL(new Blob([data], { type: 'application/vnd.ms-excel' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'leads.xls';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch { toast('error', 'Failed to export Excel'); }
  };

  const handlePdfExport = () => {
    // Build a printable HTML table from current leads and open print dialog
    const cols = ['S.No', 'Name', 'Phone', 'Email', 'Source', 'Status', 'Country', 'Assigned To'];
    const rows = leads.map((l: any, i: number) => [
      i + 1,
      l.name,
      Array.isArray(l.phones) ? l.phones.join(', ') : l.phones || '',
      Array.isArray(l.emails) ? l.emails.join(', ') : l.emails || '',
      l.source || '',
      l.status || '',
      l.country || '',
      l.assignedTo?.name || '',
    ]);

    const html = `<!DOCTYPE html><html><head><title>Leads Export</title>
      <style>body{font-family:Arial,sans-serif;margin:20px}table{width:100%;border-collapse:collapse;font-size:12px}
      th,td{border:1px solid #ddd;padding:6px 8px;text-align:left}th{background:#6366F1;color:#fff}
      tr:nth-child(even){background:#f9fafb}h1{color:#6366F1;font-size:18px}</style></head>
      <body><h1>BrixLead — Leads Report</h1><p style="color:#666;font-size:12px">${formatDate(new Date())} | ${leads.length} leads</p>
      <table><thead><tr>${cols.map(c => `<th>${c}</th>`).join('')}</tr></thead>
      <tbody>${rows.map(r => `<tr>${r.map(c => `<td>${c}</td>`).join('')}</tr>`).join('')}</tbody></table></body></html>`;

    const w = window.open('', '_blank');
    if (w) { w.document.write(html); w.document.close(); w.print(); }
  };

  // Open the styled confirm modal — actual DELETE happens in
  // handleConfirmDelete below. Previously used native window.confirm()
  // which looks nothing like every other confirmation on the app.
  const handleDelete = (lead: { id: string; name: string }) => {
    setConfirmDeleteLead({ id: lead.id, name: lead.name || 'this lead' });
  };

  const handleConfirmDelete = async () => {
    if (!confirmDeleteLead) return;
    setConfirmDeleteLoading(true);
    try {
      await api.delete(`/leads/${confirmDeleteLead.id}`);
      toast('success', 'Lead deleted');
      setConfirmDeleteLead(null);
      fetchLeads(filters);
    } catch (err: any) {
      toast('error', err?.response?.data?.message || 'Failed to delete lead');
    } finally {
      setConfirmDeleteLoading(false);
    }
  };

  const getStatusClasses = (status: string): string => {
    const found = statusOptions.find(s => s.name.toLowerCase() === status.toLowerCase());
    if (found?.color) {
      return 'border';
    }
    return 'bg-slate-100 text-slate-500 border border-slate-200';
  };

  const getStatusStyle = (status: string): React.CSSProperties => {
    const found = statusOptions.find(s => s.name.toLowerCase() === status.toLowerCase());
    if (found?.color) {
      return {
        backgroundColor: `${found.color}15`,
        color: found.color,
        borderColor: `${found.color}40`,
      };
    }
    return {};
  };

  return (
    <DashboardLayout title="All Leads">
      <div className="space-y-4">
        {/* Page Header */}
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-xl font-bold text-slate-900">All Leads</h1>
            <span className="rounded-full bg-indigo-50 px-2.5 py-0.5 text-xs font-medium text-[#6366F1]">
              {total.toLocaleString('en-IN')} leads
            </span>
            {/* Financial Year selector */}
            {financialYears.length > 0 && (
              <SelectField
                value={activeFYId}
                onChange={e => { setActiveFYId(e.target.value); }}
                className="h-9 px-3 border border-slate-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-indigo-400"
              >
                <option value="">All Financial Years</option>
                {financialYears.map(fy => (
                  <option key={fy.id} value={fy.id}>{fy.name}</option>
                ))}
              </SelectField>
            )}
          </div>
          <div className="flex items-center gap-2">
            {/* View toggle */}
            <div className="flex rounded-lg border border-slate-200 bg-white p-0.5">
              <button onClick={() => setViewMode('table')}
                className={`inline-flex items-center gap-1.5 rounded-md px-3 h-8 text-sm ${viewMode === 'table' ? 'bg-indigo-50 text-[#6366F1] font-medium' : 'text-slate-500 hover:bg-slate-50'}`}>
                <LayoutList className="h-4 w-4" /> List
              </button>
              <button onClick={() => setViewMode('kanban')}
                className={`inline-flex items-center gap-1.5 rounded-md px-3 h-8 text-sm ${viewMode === 'kanban' ? 'bg-indigo-50 text-[#6366F1] font-medium' : 'text-slate-500 hover:bg-slate-50'}`}>
                <LayoutGrid className="h-4 w-4" /> Board
              </button>
            </div>
            <Button
              onClick={() => setShowForm(true)}
              className="bg-[#6366F1] hover:bg-[#5254cc] text-white h-9 px-4 text-sm font-medium"
            >
              <Plus className="h-4 w-4 mr-1.5" />
              Add Lead
            </Button>
          </div>
        </div>

        {/* Vendor update banner */}
        {showVendorUpdateBanner && (
          <div className="flex items-center justify-between bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 text-sm">
            <span className="text-amber-800">Your admin updated the default column layout.</span>
            <div className="flex items-center gap-3">
              <button onClick={resetToDefault} className="text-[#6366F1] font-medium text-xs hover:underline">
                Update to new defaults
              </button>
              <button onClick={dismissVendorBanner} className="text-slate-500 text-xs hover:underline">
                Keep mine
              </button>
            </div>
          </div>
        )}

        {/* Top bar: Filter toggle */}
        <div className="flex items-center gap-3 flex-wrap">
          <Button
            variant={showFilters ? 'default' : 'outline'}
            size="sm"
            onClick={() => setShowFilters(v => !v)}
            className="relative"
          >
            <SlidersHorizontal className="h-4 w-4 mr-1.5" />
            Filter
            {activeFilterCount > 0 && (
              <span className="ml-1.5 bg-white text-indigo-600 text-[10px] font-bold rounded-full h-4 w-4 flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </Button>

          {/* Sort button */}
          <div className="relative" ref={sortWrapperRef}>
            <Button variant={showSort ? 'default' : 'outline'} size="sm" onClick={() => setShowSort(v => !v)}>
              <ArrowUpDown className="h-4 w-4 mr-1.5" /> Sort
              {sortField && (
                <span className="ml-1 text-xs opacity-80">
                  · {SORT_LABELS[sortField] ?? sortField} {sortOrder === 'asc' ? '↑' : '↓'}
                </span>
              )}
            </Button>
            <Floating
              open={showSort}
              anchorRef={sortWrapperRef}
              onClose={() => setShowSort(false)}
              matchWidth={false}
              className="bg-white border border-slate-200 rounded-xl shadow-xl p-4"
              style={{ width: 320 }}
            >
                <p className="text-sm font-bold text-slate-800 mb-3">Sort By</p>
                <div className="flex gap-3">
                  <SelectField value={sortField} onChange={e => setSortField(e.target.value)}
                    className="flex-1 h-9 border border-slate-200 rounded-lg px-2 text-sm">
                    <option value="">None</option>
                    <option value="name">Name</option>
                    <option value="createdAt">Created Date</option>
                    <option value="updatedAt">Updated Date</option>
                    <option value="status">Status</option>
                    <option value="priority">Priority</option>
                    <option value="source">Source</option>
                    <option value="country">Country</option>
                    <option value="city">City</option>
                  </SelectField>
                  <SelectField value={sortOrder} onChange={e => setSortOrder(e.target.value as 'asc' | 'desc')}
                    className="w-28 h-9 border border-slate-200 rounded-lg px-2 text-sm">
                    <option value="asc">Ascending</option>
                    <option value="desc">Descending</option>
                  </SelectField>
                </div>
                <div className="flex justify-end gap-2 mt-3">
                  <Button variant="outline" size="sm" onClick={() => { setSortField(''); setSortOrder('desc'); setShowSort(false); }}>Cancel</Button>
                  <Button size="sm" onClick={() => { fetchLeads(filters); setShowSort(false); }}>Apply</Button>
                </div>
            </Floating>
          </div>

          {/* Quick search — name, phone, email or lead number (debounced) */}
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              value={filters.search}
              onChange={e => setFilters(f => ({ ...f, search: e.target.value, page: 1 }))}
              placeholder="Search name, phone, email or lead no"
              className="h-9 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-8 text-sm focus:outline-none focus:ring-2 focus:ring-[#6366F1]"
            />
            {filters.search && (
              <button
                onClick={() => setFilters(f => ({ ...f, search: '', page: 1 }))}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-slate-400 hover:text-slate-700"
                aria-label="Clear search"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          {activeFilterCount > 0 && (
            <button onClick={clearFilters} className="text-xs text-red-500 hover:underline">Clear all</button>
          )}
        </div>

        {/* Main content: Sidebar filter + Table */}
        <div className="flex gap-4">
          {/* Left sidebar filter panel
              - Desktop (md+): inline sidebar next to the table
              - Mobile: fixed full-screen sheet (the inline 256px sidebar
                ate half the phone screen and left the table unreadable). */}
          {showFilters && (
            <>
              <div
                className="md:hidden fixed inset-0 bg-black/40 z-30"
                onClick={() => setShowFilters(false)}
              />
              <div className="w-full md:w-64 flex-shrink-0 bg-white border border-slate-200 md:rounded-xl overflow-auto fixed md:static inset-y-0 left-0 z-40 md:z-auto max-w-[90vw] md:max-w-none shadow-2xl md:shadow-none">
                {/* Mobile-only close bar */}
                <div className="md:hidden flex items-center justify-between px-4 h-12 border-b border-slate-100 sticky top-0 bg-white z-10">
                  <span className="text-sm font-semibold text-slate-700">Filters</span>
                  <button
                    onClick={() => setShowFilters(false)}
                    className="p-1 text-slate-400 hover:text-slate-700"
                    aria-label="Close filters"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              {/* Saved Filters */}
              <div className="border-b border-slate-100">
                <button
                  onClick={() => setFilterSection(s => ({ ...s, saved: !s.saved }))}
                  className="w-full flex items-center justify-between px-4 py-3 text-sm font-bold text-slate-800 hover:bg-slate-50"
                >
                  <span>Saved Filters <span className="text-xs font-normal text-slate-400 ml-1">{savedFilters.length}</span></span>
                  <ChevronDown className={`h-3.5 w-3.5 text-slate-400 transition-transform ${filterSection.saved ? '' : '-rotate-90'}`} />
                </button>
                {filterSection.saved && (
                  <div className="px-2 pb-2 space-y-0.5">
                    {savedFilters.length === 0 ? (
                      <p className="text-xs text-slate-400 px-2 py-2">No saved filters</p>
                    ) : savedFilters.map(sf => (
                      <div key={sf.id} className="flex items-center justify-between px-2 py-1.5 rounded-md hover:bg-slate-50 group cursor-pointer"
                        onClick={() => handleApplySavedFilter(sf)}>
                        <span className="text-sm text-slate-700 truncate">{sf.name}</span>
                        <button onClick={e => { e.stopPropagation(); handleDeleteSavedFilter(sf.id); }}
                          className="text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100 text-xs">&times;</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Filter search */}
              <div className="px-3 py-2 border-b border-slate-100">
                <div className="relative">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                  <input
                    className="w-full h-8 pl-8 pr-2 border border-slate-200 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    placeholder="Search filters..."
                    value={filterSearch}
                    onChange={e => setFilterSearch(e.target.value)}
                  />
                </div>
              </div>

              {/* Filter fields in collapsible sections */}
              <div className="overflow-y-auto max-h-[calc(100vh-300px)]">
                {/* Basic Filters */}
                <FilterSection title="Basic Filters" open={filterSection.basic} onToggle={() => setFilterSection(s => ({ ...s, basic: !s.basic }))} search={filterSearch}>
                  <SidebarFilterInput label="Name" value={filters.name} onChange={v => setFilters(f => ({ ...f, name: v, page: 1 }))} />
                  <SidebarFilterInput label="Phone" value={filters.phone} onChange={v => setFilters(f => ({ ...f, phone: v, page: 1 }))} />
                  <SidebarFilterInput label="Email" value={filters.email} onChange={v => setFilters(f => ({ ...f, email: v, page: 1 }))} />
                  <SidebarSelect label="Status" value={filters.status} onChange={v => setFilters(f => ({ ...f, status: v, page: 1 }))}
                    options={statusOptions.map(s => ({ value: s.name, label: s.name }))} />
                  <SidebarSelect label="Source" value={filters.source} onChange={v => setFilters(f => ({ ...f, source: v, page: 1 }))}
                    options={sourceOptions.map(s => ({ value: s, label: s }))} />
                  <SidebarSelect label="Priority" value={filters.priority} onChange={v => setFilters(f => ({ ...f, priority: v, page: 1 }))}
                    options={['High', 'Medium', 'Low'].map(p => ({ value: p, label: p }))} />
                  <SidebarSelect label="Category" value={filters.leadCategory} onChange={v => setFilters(f => ({ ...f, leadCategory: v, page: 1 }))}
                    options={['Hot', 'Warm', 'Cold'].map(c => ({ value: c, label: c }))} />
                  <SidebarSelect label="Assigned To" value={filters.assignedToId} onChange={v => setFilters(f => ({ ...f, assignedToId: v, page: 1 }))}
                    options={users.map(u => ({ value: u.id, label: u.name }))} />
                </FilterSection>

                {/* Location */}
                <FilterSection title="Location" open={filterSection.location} onToggle={() => setFilterSection(s => ({ ...s, location: !s.location }))} search={filterSearch}>
                  <SidebarFilterInput label="Country" value={filters.country} onChange={v => setFilters(f => ({ ...f, country: v, page: 1 }))} />
                  <SidebarFilterInput label="State" value={filters.state} onChange={v => setFilters(f => ({ ...f, state: v, page: 1 }))} />
                  <SidebarFilterInput label="City" value={filters.city} onChange={v => setFilters(f => ({ ...f, city: v, page: 1 }))} />
                  <SidebarFilterInput label="Zipcode" value={filters.pincode} onChange={v => setFilters(f => ({ ...f, pincode: v, page: 1 }))} />
                </FilterSection>

                {/* Date Range */}
                <FilterSection title="Date Range" open={filterSection.dates} onToggle={() => setFilterSection(s => ({ ...s, dates: !s.dates }))} search={filterSearch}>
                  <SidebarFilterInput label="Created From" value={filters.dateFrom} onChange={v => setFilters(f => ({ ...f, dateFrom: v, page: 1 }))} type="date" />
                  <SidebarFilterInput label="Created To" value={filters.dateTo} onChange={v => setFilters(f => ({ ...f, dateTo: v, page: 1 }))} type="date" />
                </FilterSection>

                {/* Campaign & Tags */}
                <FilterSection title="Campaign & Tags" open={filterSection.campaign} onToggle={() => setFilterSection(s => ({ ...s, campaign: !s.campaign }))} search={filterSearch}>
                  <SidebarFilterInput label="Campaign" value={filters.campaignName} onChange={v => setFilters(f => ({ ...f, campaignName: v, page: 1 }))} />
                  <SidebarFilterInput label="Hashtag" value={filters.hashtag} onChange={v => setFilters(f => ({ ...f, hashtag: v, page: 1 }))} />
                  <SidebarFilterInput label="Notes" value={filters.notes} onChange={v => setFilters(f => ({ ...f, notes: v, page: 1 }))} />
                </FilterSection>

                {/* UTM Parameters */}
                <FilterSection title="UTM Parameters" open={filterSection.utm} onToggle={() => setFilterSection(s => ({ ...s, utm: !s.utm }))} search={filterSearch}>
                  <SidebarFilterInput label="UTM Source" value={filters.utmSource} onChange={v => setFilters(f => ({ ...f, utmSource: v, page: 1 }))} />
                  <SidebarFilterInput label="UTM Medium" value={filters.utmMedium} onChange={v => setFilters(f => ({ ...f, utmMedium: v, page: 1 }))} />
                  <SidebarFilterInput label="UTM Campaign" value={filters.utmCampaign} onChange={v => setFilters(f => ({ ...f, utmCampaign: v, page: 1 }))} />
                  <SidebarFilterInput label="UTM Term" value={filters.utmTerm} onChange={v => setFilters(f => ({ ...f, utmTerm: v, page: 1 }))} />
                  <SidebarFilterInput label="UTM Content" value={filters.utmContent} onChange={v => setFilters(f => ({ ...f, utmContent: v, page: 1 }))} />
                </FilterSection>
              </div>

              {/* Actions */}
              <div className="border-t border-slate-100 px-3 py-3 space-y-2">
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    className="flex-1 h-8 text-xs"
                    onClick={() => {
                      fetchLeads(filters);
                      // On mobile the filter panel is a full-screen drawer —
                      // close it after applying so the user sees the results.
                      if (typeof window !== 'undefined' && window.innerWidth < 768) {
                        setShowFilters(false);
                      }
                    }}
                  >
                    Apply
                  </Button>
                  <Button variant="outline" size="sm" className="flex-1 h-8 text-xs" onClick={clearFilters}>Clear</Button>
                </div>
                {showSaveInput ? (
                  <div className="flex gap-1.5">
                    <input value={saveFilterName} onChange={e => setSaveFilterName(e.target.value)}
                      placeholder="Filter name" className="flex-1 h-7 px-2 border border-slate-200 rounded text-xs"
                      onKeyDown={e => { if (e.key === 'Enter') handleSaveFilter(); }} autoFocus />
                    <button onClick={handleSaveFilter} disabled={!saveFilterName.trim()}
                      className="text-xs text-indigo-600 font-medium disabled:opacity-50">Save</button>
                    <button onClick={() => { setShowSaveInput(false); setSaveFilterName(''); }}
                      className="text-xs text-slate-400">X</button>
                  </div>
                ) : (
                  <button onClick={() => setShowSaveInput(true)}
                    className="w-full flex items-center justify-center gap-1 h-8 text-xs font-medium text-slate-600 border border-slate-200 rounded-lg hover:bg-slate-50">
                    <Bookmark className="h-3 w-3" /> Save Filter
                  </button>
                )}
              </div>
            </div>
            </>
          )}

          {/* Right: Table */}
          <div className="flex-1 min-w-0">
        {/* Table Card */}
        <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
          {/* Toolbar — wraps + horizontal scrolls on mobile so bulk-action buttons stay reachable */}
          <div className="flex items-center justify-between gap-2 px-4 py-2.5 border-b border-slate-100 bg-slate-50/60 overflow-x-auto">
            {/* Left: bulk actions only once leads are ticked; a hint otherwise */}
            {someSelected ? (
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-[#6366F1] whitespace-nowrap">{selectedIds.size} selected</span>
                <button
                  onClick={() => setSelectedIds(new Set())}
                  className="text-xs text-slate-400 hover:text-slate-600 px-1"
                >
                  Clear
                </button>
                <div className="w-px h-4 bg-slate-200 mx-1" />
                <button
                  onClick={() => setShowMoveDrawer(true)}
                  className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition-colors"
                >
                  <ArrowRightLeft className="h-3.5 w-3.5" /> Assign / Move
                </button>
                <button
                  disabled={selectedIds.size < 2}
                  onClick={() => setShowMergeDrawer(true)}
                  title={selectedIds.size < 2 ? 'Select 2 or more leads to merge' : 'Merge selected leads'}
                  className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  <GitMerge className="h-3.5 w-3.5" /> Merge
                </button>
              </div>
            ) : (
              <p className="hidden sm:block text-sm text-slate-500 whitespace-nowrap">Tick leads in the list to assign, move or merge them together.</p>
            )}

            {/* Right: columns + export */}
            <div className="flex items-center gap-2">
              {/* Columns dropdown */}
              <button
                ref={columnsBtnRef}
                onClick={openColumnsMenu}
                className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md border bg-white transition-colors ${showColumnsMenu ? 'border-[#6366F1] text-[#6366F1]' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
              >
                <Columns className="h-3.5 w-3.5" />
                Columns
                <span className="ml-0.5 text-[10px] bg-slate-100 text-slate-500 rounded px-1 py-px font-semibold">
                  {visibleColumns.length}/{ALL_LEAD_COLUMNS.length}
                </span>
                <ChevronDown className="h-3 w-3" />
              </button>

              {showColumnsMenu && (
                <div
                  ref={columnsDropdownRef}
                  style={{
                    position: 'fixed',
                    top: columnsMenuPos.top,
                    right: columnsMenuPos.right,
                    zIndex: 9999,
                  }}
                  className="bg-white border border-slate-200 rounded-xl shadow-xl w-72"
                >
                  {/* Header */}
                  <div className="flex items-center justify-between px-3 py-2 border-b border-slate-100">
                    <button
                      onClick={selectAllColumns}
                      className="text-xs text-[#6366F1] font-semibold hover:underline"
                    >
                      Select All
                    </button>
                    <span className="text-[11px] text-slate-400 font-medium">
                      {visibleColumns.length} of {ALL_LEAD_COLUMNS.length} visible
                    </span>
                    <button
                      onClick={resetToDefault}
                      className="text-xs text-slate-500 font-semibold hover:underline"
                    >
                      Reset Default
                    </button>
                  </div>

                  {/* Column checkboxes — 2 column grid */}
                  <div className="p-2 grid grid-cols-2 gap-0.5 max-h-96 overflow-y-auto">
                    {ALL_LEAD_COLUMNS.map(col => (
                      <label
                        key={col.key}
                        className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-slate-50 cursor-pointer group"
                      >
                        <input
                          type="checkbox"
                          checked={visibleColumns.includes(col.key)}
                          onChange={() => toggleColumn(col.key)}
                          className="h-3.5 w-3.5 rounded border-slate-300 text-[#6366F1] accent-[#6366F1] cursor-pointer"
                        />
                        <span className="text-xs text-slate-700 group-hover:text-slate-900 select-none">
                          {col.label}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              )}


              <button onClick={() => setShowImportModal(true)} className="flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 transition-colors">
                <Upload className="h-3.5 w-3.5" /> Import
              </button>
              {/* Export — CSV / Excel / PDF in one menu */}
              <div ref={exportWrapperRef} className="relative">
                <button
                  onClick={() => setShowExport(v => !v)}
                  className={`flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md border bg-white transition-colors ${showExport ? 'border-[#6366F1] text-[#6366F1]' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}
                >
                  <Download className="h-3.5 w-3.5" /> Export <ChevronDown className="h-3 w-3" />
                </button>
                <Floating
                  open={showExport}
                  anchorRef={exportWrapperRef}
                  onClose={() => setShowExport(false)}
                  matchWidth={false}
                  className="bg-white border border-slate-200 rounded-xl shadow-xl py-1"
                  style={{ width: 168 }}
                >
                  <button onClick={() => { setShowExport(false); handleCsvExport(); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
                    <FileText className="h-4 w-4 text-red-500" /> CSV
                  </button>
                  <button onClick={() => { setShowExport(false); handleExcelExport(); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
                    <FileSpreadsheet className="h-4 w-4 text-emerald-500" /> Excel
                  </button>
                  <button onClick={() => { setShowExport(false); handlePdfExport(); }} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
                    <Download className="h-4 w-4 text-blue-500" /> PDF
                  </button>
                </Floating>
              </div>
            </div>
          </div>

          {/* Kanban View */}
          {viewMode === 'kanban' && (
            <KanbanBoard
              leads={leads}
              statuses={statuses}
              onStatusChange={async (leadId, newStatus) => {
                try {
                  await api.patch(`/leads/${leadId}`, { status: newStatus });
                  toast('success', `Status updated to ${newStatus}`);
                  fetchLeads(filters);
                } catch { toast('error', 'Failed to update status'); }
              }}
            />
          )}

          {/* Table (hidden in kanban mode) */}
          {viewMode !== 'table' ? null : isLoading ? (
            // Skeleton instead of spinner — communicates layout, reduces perceived wait.
            // Mobile: card skeletons; desktop: table-row skeletons (count matches the
            // typical first-page query of 25, capped at 8 to avoid janky long lists).
            <>
              <div className="md:hidden">
                {Array.from({ length: 6 }).map((_, i) => <CardRowSkeleton key={i} />)}
              </div>
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <tbody>
                    {Array.from({ length: 8 }).map((_, i) => (
                      <TableRowSkeleton key={i} columns={8} />
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          ) : leads.length === 0 ? (
            // Two empty cases: filter applied (zero results) vs. true empty (new tenant).
            // The former gets a "clear filters" CTA, the latter prompts the create flow.
            activeFilterCount > 0 || filters.search ? (
              <EmptyState
                icon={FilterX}
                title="No leads match these filters"
                description="Try widening your filters or clearing them to see all leads."
                action={
                  <Button variant="outline" onClick={clearFilters}>Clear filters</Button>
                }
              />
            ) : (
              <EmptyState
                icon={Inbox}
                title="No leads yet"
                description="Add your first lead manually, import from a CSV, or connect an integration to start capturing leads automatically."
                action={
                  <Button onClick={() => setShowForm(true)}>
                    <Plus className="h-3.5 w-3.5 mr-1.5" /> Add your first lead
                  </Button>
                }
                helperText="You can also bulk-import via CSV from the menu above."
              />
            )
          ) : (
            <>
            {/* Mobile card list — shown only below md. A long table would
                force horizontal scrolling every row; cards show the essentials
                at a glance and link to the full detail page. */}
            <div className="md:hidden divide-y divide-slate-100">
              {leads.map((lead, idx) => {
                const isSelected = selectedIds.has(lead.id);
                // LeadPhone/LeadEmail child rows; backend orders primary first.
                const phoneStr = formatLeadPhone(lead.phones?.[0]);
                const emailStr = lead.emails?.[0]?.email || '';
                return (
                  <div
                    key={lead.id}
                    className={`px-4 py-3 ${isSelected ? 'bg-indigo-50/40' : ''}`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        className="h-4 w-4 mt-1 rounded border-slate-300 text-[#6366F1]"
                        checked={isSelected}
                        onChange={() => toggleSelect(lead.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                      <div className="flex-1 min-w-0" onClick={() => navigate(`/leads/${lead.id}`)}>
                        {/* Lead no + source + created (the "Leads" column) */}
                        <p className="text-xs">
                          <span className="text-slate-400 mr-1.5">{pageOffset + idx + 1}.</span>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setDrawerLeadId(lead.id); }}
                            className="font-mono text-[#6366F1] hover:underline"
                          >
                            {lead.leadNo ? `#${lead.leadNo}` : `#${lead.id.slice(-6).toUpperCase()}`}
                          </button>
                          <button
                            type="button"
                            title="Copy lead number"
                            onClick={(e) => { e.stopPropagation(); copyLeadNo(lead); }}
                            className="ml-1 inline-flex align-middle p-1 rounded text-slate-400 hover:text-[#6366F1]"
                          >
                            <Copy className="h-3 w-3" />
                          </button>
                        </p>
                        <p className="text-[11px] text-slate-500 mt-0.5">
                          {[lead.source, formatDateTime(lead.createdAt)].filter(Boolean).join(' · ')}
                        </p>
                        <p className={`mt-0.5 inline-flex items-center gap-1 text-[11px] ${lead.assignedTo?.name ? 'text-slate-600' : 'text-slate-400'}`}>
                          <UserIcon className="h-3 w-3" /> {lead.assignedTo?.name || 'Unassigned'}
                        </p>

                        {/* Contact */}
                        <p className="mt-2 font-medium text-slate-800 text-sm truncate">{lead.name}</p>
                        {phoneStr && <p className="text-xs text-slate-600 truncate mt-0.5">{phoneStr}</p>}
                        {emailStr && <p className="text-xs text-slate-500 truncate mt-0.5">{emailStr}</p>}
                        {[lead.city, lead.state, lead.country].some(Boolean) && (
                          <p className="text-xs text-slate-500 truncate mt-0.5">{[lead.city, lead.state, lead.country].filter(Boolean).join(' | ')}</p>
                        )}

                        {/* Status, then latest follow-up */}
                        {lead.status && (
                          <span
                            className={`mt-2 inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize ${getStatusClasses(lead.status)}`}
                            style={getStatusStyle(lead.status)}
                          >
                            {lead.status}
                          </span>
                        )}
                        {vis('followup') && (lead as any).followups?.[0] && <div className="mt-1.5">{renderLatestFollowup(lead)}</div>}

                        {/* Latest meeting / demo */}
                        {vis('meeting') && (
                          <div className="mt-2 rounded-lg bg-slate-50 px-2.5 py-2" onClick={(e) => e.stopPropagation()}>
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-400 mb-1">Meeting</p>
                            {renderMeeting(lead)}
                          </div>
                        )}
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                          setActionMenu((prev) => prev?.leadId === lead.id ? null : { leadId: lead.id, rect });
                        }}
                        className="p-1.5 hover:bg-slate-100 rounded-lg flex-shrink-0"
                      >
                        <MoreVertical className="h-4 w-4 text-slate-400" />
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Desktop table — hidden below md */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="sticky top-0 z-10">
                  <TableHeadRow className="border-b-2 border-slate-200">
                    <SortTh sortable={false} className="w-10" label={
                      <input
                        type="checkbox"
                        className="h-4 w-4 rounded border-slate-300 text-[#6366F1] focus:ring-[#6366F1]"
                        checked={allSelected}
                        onChange={toggleSelectAll}
                      />
                    } />
                    {vis('sno') && <SortTh sortable={false} className="w-12 px-2" label="S.No" />}
                    {/* Lead No + Source share one column; each part still follows the Columns picker. */}
                    {(vis('leadNo') || vis('source')) && <SortTh label={vis('leadNo') ? 'Lead No' : 'Lead Source'} sortKey={vis('leadNo') ? 'leadNo' : 'source'} sort={tableSort} onSort={onTableSort} />}
                    {vis('contact') && <SortTh label="Name" sortKey="name" sort={tableSort} onSort={onTableSort} />}
                    {vis('email') && <SortTh sortable={false} label="Email" />}
                    {vis('phone') && <SortTh sortable={false} label="Phone" />}
                    {vis('created') && <SortTh label="Created" sortKey="createdAt" sort={tableSort} onSort={onTableSort} />}
                    {vis('status') && <SortTh label="Status" sortKey="status" sort={tableSort} onSort={onTableSort} />}
                    {vis('followup') && <SortTh sortable={false} label="Follow-up" />}
                    {vis('meeting') && <SortTh sortable={false} label="Meeting" />}
                    {vis('score') && <SortTh label="Score" sortKey="score" sort={tableSort} onSort={onTableSort} />}
                    {vis('priority') && <SortTh label="Priority" sortKey="priority" sort={tableSort} onSort={onTableSort} />}
                    {vis('country') && <SortTh label="Country" sortKey="country" sort={tableSort} onSort={onTableSort} />}
                    {vis('state') && <SortTh label="State" sortKey="state" sort={tableSort} onSort={onTableSort} />}
                    {vis('city') && <SortTh label="City" sortKey="city" sort={tableSort} onSort={onTableSort} />}
                    {vis('zipcode') && <SortTh label="Zipcode" sortKey="pincode" sort={tableSort} onSort={onTableSort} />}
                    {vis('assignedTo') && <SortTh label="Assigned To" sortKey="assignedTo.name" sort={tableSort} onSort={onTableSort} />}
                    {vis('campaign') && <SortTh label="Campaign" sortKey="campaignName" sort={tableSort} onSort={onTableSort} />}
                    {vis('leadCategory') && <SortTh label="Category" sortKey="leadCategory" sort={tableSort} onSort={onTableSort} />}
                    {vis('leadColor') && <SortTh label="Color" sortKey="leadColor" sort={tableSort} onSort={onTableSort} />}
                    {vis('hashtags') && <SortTh sortable={false} label="Hashtags" />}
                    {vis('description') && <SortTh sortable={false} label="Description" />}
                    {/* Actions stays frozen on the right while the table scrolls sideways. */}
                    <SortTh sortable={false} align="right" label="Actions" className="sticky right-0 z-20 bg-[#6366F1] shadow-[-6px_0_8px_-6px_rgba(15,23,42,0.35)]" />
                  </TableHeadRow>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {sortedLeads.map((lead, idx) => {
                    const isSelected = selectedIds.has(lead.id);
                    return (
                      <tr
                        key={lead.id}
                        className={`group align-top [&>td]:align-top hover:bg-slate-50/70 transition-colors ${isSelected ? 'bg-indigo-50/40' : ''}`}
                      >
                        <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                          <input
                            type="checkbox"
                            className="h-4 w-4 rounded border-slate-300 text-[#6366F1] focus:ring-[#6366F1]"
                            checked={isSelected}
                            onChange={() => toggleSelect(lead.id)}
                          />
                        </td>
                        {vis('sno') && (
                          <td className="px-2 py-3 text-slate-400 text-xs font-medium">
                            {pageOffset + idx + 1}
                          </td>
                        )}
                        {(vis('leadNo') || vis('source')) && (
                          <td className="px-4 py-3 whitespace-nowrap">
                            {vis('leadNo') && (
                              <div>
                                <button
                                  type="button"
                                  onClick={() => setDrawerLeadId(lead.id)}
                                  className="font-mono text-xs text-[#6366F1] hover:underline"
                                >
                                  {lead.leadNo ? `#${lead.leadNo}` : `#${lead.id.slice(-6).toUpperCase()}`}
                                </button>
                                <button
                                  type="button"
                                  title="Copy lead number"
                                  onClick={() => copyLeadNo(lead)}
                                  className="ml-1 inline-flex align-middle p-0.5 rounded text-slate-400 hover:text-[#6366F1]"
                                >
                                  <Copy className="h-3 w-3" />
                                </button>
                              </div>
                            )}
                            {vis('source') && (
                              <p className={`text-sm text-slate-500 ${vis('leadNo') ? 'mt-0.5' : ''}`}>{lead.source || '—'}</p>
                            )}
                          </td>
                        )}
                        {vis('contact') && (
                          <td className="px-4 py-3">
                            <p
                              className="font-medium text-slate-800 text-sm leading-tight whitespace-nowrap cursor-pointer hover:text-[#6366F1] transition-colors"
                              onClick={() => setDrawerLeadId(lead.id)}
                            >
                              {lead.name}
                            </p>
                          </td>
                        )}
                        {vis('email') && (
                          <td className="px-4 py-3 text-slate-600 text-sm whitespace-nowrap">
                            {lead.emails?.[0]?.email || '—'}
                          </td>
                        )}
                        {vis('phone') && (
                          <td className="px-4 py-3 text-slate-600 text-sm whitespace-nowrap">
                            {formatLeadPhone(lead.phones?.[0]) || '—'}
                          </td>
                        )}
                        {vis('created') && (
                          <td className="px-4 py-3 text-slate-500 text-xs whitespace-nowrap">
                            {formatDateTime(lead.createdAt)}
                          </td>
                        )}
                        {vis('status') && (
                          <td className="px-4 py-3 align-top whitespace-nowrap">
                            {lead.status ? (
                              <span
                                className={`inline-flex items-center whitespace-nowrap rounded-full px-2 py-0.5 text-xs font-medium capitalize ${getStatusClasses(lead.status)}`}
                                style={getStatusStyle(lead.status)}
                              >
                                {lead.status}
                              </span>
                            ) : '—'}
                          </td>
                        )}
                        {vis('followup') && (
                          <td className="px-4 py-3 align-top min-w-[200px] max-w-[280px]">
                            {renderLatestFollowup(lead) ?? <span className="text-sm text-slate-400">—</span>}
                          </td>
                        )}
                        {vis('meeting') && (
                          <td className="px-4 py-3 align-top">{renderMeeting(lead)}</td>
                        )}
                        {vis('score') && (
                          <td className="px-4 py-3 align-top">
                            <ScoreBadge score={lead.score ?? 0} size="sm" showLabel={false} />
                          </td>
                        )}
                        {vis('priority') && (
                          <td className="px-4 py-3 align-top">
                            {lead.priority ? (
                              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${priorityColors[lead.priority.toLowerCase()] || 'bg-slate-100 text-slate-500 border border-slate-200'}`}>
                                {getPriorityLabel(lead.priority)}
                              </span>
                            ) : '—'}
                          </td>
                        )}
                        {vis('country') && <td className="px-4 py-3 text-slate-500 text-sm">{lead.country || '—'}</td>}
                        {vis('state') && <td className="px-4 py-3 text-slate-500 text-sm">{lead.state || '—'}</td>}
                        {vis('city') && <td className="px-4 py-3 text-slate-500 text-sm">{lead.city || '—'}</td>}
                        {vis('zipcode') && <td className="px-4 py-3 align-top text-slate-500 text-sm">{lead.pincode || '—'}</td>}
                        {vis('assignedTo') && (
                          <td className="px-4 py-3 text-slate-600 text-sm">{lead.assignedTo?.name || '—'}</td>
                        )}
                        {vis('campaign') && (
                          <td className="px-4 py-3 text-slate-500 text-sm">{lead.campaignName || '—'}</td>
                        )}
                        {vis('leadCategory') && (
                          <td className="px-4 py-3 text-slate-500 text-sm">{lead.leadCategory || '—'}</td>
                        )}
                        {vis('leadColor') && (
                          <td className="px-4 py-3">
                            {lead.leadColor ? (
                              <div className="flex items-center gap-1.5">
                                <span
                                  className="h-3.5 w-3.5 rounded-full flex-shrink-0 border border-slate-200"
                                  style={{ backgroundColor: lead.leadColor }}
                                />
                                <span className="text-xs text-slate-500 font-mono">{lead.leadColor}</span>
                              </div>
                            ) : '—'}
                          </td>
                        )}
                        {vis('hashtags') && (
                          <td className="px-4 py-3">
                            {Array.isArray(lead.hashtags) && lead.hashtags.length > 0 ? (
                              <div className="flex flex-wrap gap-1 max-w-[160px]">
                                {lead.hashtags.slice(0, 3).map((tag: string, i: number) => (
                                  <span key={i} className="inline-flex items-center rounded-full bg-slate-100 text-slate-600 text-[10px] px-1.5 py-0.5 font-medium">
                                    #{tag}
                                  </span>
                                ))}
                                {lead.hashtags.length > 3 && (
                                  <span className="text-[10px] text-slate-400">+{lead.hashtags.length - 3}</span>
                                )}
                              </div>
                            ) : '—'}
                          </td>
                        )}
                        {vis('description') && (
                          <td className="px-4 py-3 text-slate-500 text-xs max-w-[200px]">
                            {lead.notes ? (
                              <span className="line-clamp-2">{lead.notes}</span>
                            ) : '—'}
                          </td>
                        )}
                        <td className={`sticky right-0 z-10 px-4 py-3 text-right shadow-[-6px_0_8px_-6px_rgba(15,23,42,0.18)] transition-colors ${isSelected ? 'bg-indigo-50' : 'bg-white group-hover:bg-slate-50'}`}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                              setActionMenu(prev => prev?.leadId === lead.id ? null : { leadId: lead.id, rect });
                            }}
                            className="p-1.5 hover:bg-slate-100 rounded-lg"
                          >
                            <MoreVertical className="h-4 w-4 text-slate-400" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            </>
          )}

          {drawerLeadId && (
            <LeadDetailDrawer
              leadId={drawerLeadId}
              onClose={() => setDrawerLeadId(null)}
              onChanged={() => fetchLeads()}
            />
          )}

          {/* Action dropdown menu */}
          {actionMenu && (() => {
            const lead = leads.find((l: any) => l.id === actionMenu.leadId);
            if (!lead) return null;
            // Flip upward when there isn't enough space below the trigger.
            const links = bookingLinks[lead.id];
            const linkRows = links && links !== 'loading' ? links.events.length + 1 : links === 'loading' ? 2 : 0;
            const MENU_H = 292 + linkRows * 36; // ~7 items × 40px + divider + padding (+ booking links)
            const MENU_W = 208; // w-52
            const spaceBelow = window.innerHeight - actionMenu.rect.bottom;
            const menuTop = spaceBelow < MENU_H + 8
              ? Math.max(8, actionMenu.rect.top - MENU_H - 4)
              : actionMenu.rect.bottom + 4;
            const menuLeft = Math.max(8, Math.min(
              actionMenu.rect.left - 140,
              window.innerWidth - MENU_W - 8,
            ));
            return (
              <div
                className="fixed z-50 bg-white rounded-xl border border-slate-200 shadow-xl py-1.5 w-52"
                style={{ top: menuTop, left: menuLeft }}
                onClick={() => setActionMenu(null)}
              >
                <button onClick={() => navigate(`/leads/${lead.id}`)}
                  className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">
                  <Eye className="h-4 w-4" /> View
                </button>
                <button onClick={() => { setEditLead(lead); setActionMenu(null); }}
                  className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">
                  <Pencil className="h-4 w-4" /> Edit
                </button>
                <button onClick={() => { setFollowupLead(lead); setActionMenu(null); }}
                  className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">
                  <MessageSquarePlus className="h-4 w-4" /> Add Followup
                </button>
                {/* Outbound contact actions — both gated server-side via the
                    PlanFeatureGuard, so a free tenant clicking these will hit
                    the global upgrade modal rather than a silent fail. */}
                {(() => {
                  // Use the LeadPhone child rows when present; fall back to
                  // the legacy single `phone` string for older leads that
                  // pre-date the multi-phone migration.
                  const phone =
                    formatLeadPhone(Array.isArray(lead.phones) ? lead.phones[0] : null) ||
                    (lead as any).phone ||
                    '';
                  if (!phone) return null;
                  return (
                    <>
                      <button
                        onClick={() => {
                          setMessageTarget({ channel: 'sms', leadId: lead.id, leadName: lead.name || 'Lead', phone });
                          setActionMenu(null);
                        }}
                        className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                      >
                        <MessageSquare className="h-4 w-4 text-orange-500" /> Send SMS
                      </button>
                      <button
                        onClick={() => {
                          setMessageTarget({ channel: 'whatsapp', leadId: lead.id, leadName: lead.name || 'Lead', phone });
                          setActionMenu(null);
                        }}
                        className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50"
                      >
                        <MessagesSquare className="h-4 w-4 text-emerald-600" /> Send WhatsApp
                      </button>
                    </>
                  );
                })()}
                <button onClick={() => { setLogsLeadId(lead.id); setActionMenu(null); }}
                  className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">
                  <History className="h-4 w-4" /> Logs
                </button>
                {/* Booking links: only the assigned counselor's active events. */}
                {links && (
                  <>
                    <div className="my-1 border-t border-slate-100" />
                    <p className="px-4 pt-1.5 pb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Copy booking link</p>
                    {links === 'loading' ? (
                      <p className="flex items-center gap-2 px-4 py-2 text-xs text-slate-400" onClick={(e) => e.stopPropagation()}>
                        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading…
                      </p>
                    ) : links.events.map(ev => (
                      <button key={ev.id} onClick={() => { copyBookingLink(links, ev); setActionMenu(null); }}
                        title={`Copy ${ev.name} booking link`}
                        className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-slate-700 hover:bg-slate-50">
                        <Link2 className="h-4 w-4 flex-shrink-0 text-slate-400" /> <span className="truncate">{ev.name}</span>
                      </button>
                    ))}
                  </>
                )}
                <div className="my-1 border-t border-slate-100" />
                <button onClick={() => { handleDelete(lead); setActionMenu(null); }}
                  className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-red-600 hover:bg-red-50">
                  <Trash2 className="h-4 w-4" /> Delete
                </button>
              </div>
            );
          })()}

          {/* Pagination — table mode only */}
          {viewMode === 'table' && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-slate-100">
            <div className="flex items-center gap-3">
              <p className="text-xs text-slate-400">
                Showing {Math.min((filters.page - 1) * 25 + 1, total)}–{Math.min(filters.page * 25, total)} of {total}
                {someSelected && <span className="ml-2 text-[#6366F1] font-medium">· {selectedIds.size} selected</span>}
              </p>
            </div>
            <div className="flex items-center gap-3">
              <span className="text-xs text-slate-400">Page {filters.page} of {totalPages}</span>
              <div className="flex gap-1">
                <Button
                  variant="outline" size="sm"
                  disabled={filters.page <= 1}
                  onClick={() => setFilters(f => ({ ...f, page: 1 }))}
                  className="h-8 px-2 text-xs"
                >
                  First
                </Button>
                <Button
                  variant="outline" size="sm"
                  disabled={filters.page <= 1}
                  onClick={() => setFilters(f => ({ ...f, page: f.page - 1 }))}
                  className="h-8 w-8 p-0"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  const p = filters.page <= 3
                    ? i + 1
                    : filters.page >= totalPages - 2
                      ? totalPages - 4 + i
                      : filters.page - 2 + i;
                  if (p < 1 || p > totalPages) return null;
                  return (
                    <Button
                      key={p}
                      variant={p === filters.page ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setFilters(f => ({ ...f, page: p }))}
                      className={`h-8 w-8 p-0 text-xs ${p === filters.page ? 'bg-[#6366F1] hover:bg-[#5254cc] text-white border-0' : ''}`}
                    >
                      {p}
                    </Button>
                  );
                })}
                <Button
                  variant="outline" size="sm"
                  disabled={filters.page >= totalPages}
                  onClick={() => setFilters(f => ({ ...f, page: f.page + 1 }))}
                  className="h-8 w-8 p-0"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
                <Button
                  variant="outline" size="sm"
                  disabled={filters.page >= totalPages}
                  onClick={() => setFilters(f => ({ ...f, page: totalPages }))}
                  className="h-8 px-2 text-xs"
                >
                  Last
                </Button>
              </div>
            </div>
          </div>
          )}
        </div>
          </div>{/* close flex-1 min-w-0 */}
        </div>{/* close flex gap-4 */}
      </div>

      {showForm && (
        <LeadFormModal
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); fetchLeads(filters); }}
          onPlanLimit={(msg) => setUpgradeModal(msg)}
        />
      )}

      {editLead && (
        <LeadFormModal
          lead={editLead}
          onClose={() => setEditLead(null)}
          onSaved={() => { setEditLead(null); fetchLeads(filters); }}
          onPlanLimit={(msg) => setUpgradeModal(msg)}
        />
      )}

      {followupLead && (
        <FollowupDrawer
          lead={followupLead}
          onClose={() => setFollowupLead(null)}
          onSaved={() => fetchLeads(filters)}
        />
      )}
      {logsLeadId && (
        <LeadLogsModal
          leadId={logsLeadId}
          onClose={() => setLogsLeadId(null)}
        />
      )}

      {/* Outbound SMS / WhatsApp compose modal — same component, channel
          switched via prop. Plan-gated server-side, so a free tenant clicking
          here surfaces the global upgrade modal automatically. */}
      {messageTarget && (
        <SendMessageModal
          channel={messageTarget.channel}
          leadId={messageTarget.leadId}
          leadName={messageTarget.leadName}
          phone={messageTarget.phone}
          onClose={() => setMessageTarget(null)}
          onSent={() => fetchLeads(filters)}
        />
      )}

      {/* Move Drawer */}
      {showMoveDrawer && (
        <MoveDrawer
          selectedIds={Array.from(selectedIds)}
          leads={leads}
          users={users}
          onClose={() => setShowMoveDrawer(false)}
          onMoved={() => { setShowMoveDrawer(false); setSelectedIds(new Set()); fetchLeads(filters); }}
        />
      )}

      {/* Merge Drawer */}
      {showMergeDrawer && selectedIds.size >= 2 && (
        <MergeDrawer
          leadIds={Array.from(selectedIds)}
          leads={leads}
          onClose={() => setShowMergeDrawer(false)}
          onMerged={() => { setShowMergeDrawer(false); setSelectedIds(new Set()); fetchLeads(filters); }}
        />
      )}

      {showImportModal && (
        <CsvImportModal
          onClose={() => setShowImportModal(false)}
          onImported={() => fetchLeads(filters)}
          onPlanLimit={(msg) => { setShowImportModal(false); setUpgradeModal(msg); }}
        />
      )}

      <UpgradePlanModal
        isOpen={!!upgradeModal}
        message={upgradeModal || ''}
        onClose={() => setUpgradeModal(null)}
      />

      {/* Styled delete confirmation — same ConfirmModal used on the Trash
          page and elsewhere, so users see one dialog everywhere. Deletes
          are soft (lead goes to Trash → can be restored or permanently
          deleted from there), so the copy stays "Delete" not "Delete
          Forever". */}
      <ConfirmModal
        isOpen={!!confirmDeleteLead}
        title="Delete Lead?"
        message={`Are you sure you want to delete "${confirmDeleteLead?.name ?? 'this lead'}"? It will be moved to Trash and can be restored within 30 days.`}
        confirmText="Delete"
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmDeleteLead(null)}
        isLoading={confirmDeleteLoading}
      />
    </DashboardLayout>
  );
}

// ── Sidebar filter helpers ────────────────────────────────────────────────────

function FilterSection({ title, open, onToggle, search, children }: {
  title: string; open: boolean; onToggle: () => void; search: string; children: React.ReactNode;
}) {
  // If searching, auto-show if any child label matches
  const shouldShow = !search || title.toLowerCase().includes(search.toLowerCase());
  if (!shouldShow) return null;
  return (
    <div className="border-b border-slate-100">
      <button onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-2.5 text-xs font-bold text-slate-600 uppercase tracking-wide hover:bg-slate-50">
        {title}
        <ChevronDown className={`h-3 w-3 text-slate-400 transition-transform ${open ? '' : '-rotate-90'}`} />
      </button>
      {open && <div className="px-3 pb-3 space-y-2">{children}</div>}
    </div>
  );
}

function SidebarFilterInput({ label, value, onChange, type = 'text' }: {
  label: string; value: string; onChange: (v: string) => void; type?: string;
}) {
  return (
    <div>
      <label className="text-[10px] font-normal text-slate-400 uppercase">{label}</label>
      {type === 'date' ? (
        <DateInput className="w-full" inputClassName="h-7 px-2 text-xs" value={value} onChange={onChange} placeholder={label} />
      ) : (
        <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={label}
          className="w-full h-7 px-2 border border-slate-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-indigo-400" />
      )}
    </div>
  );
}

function SidebarSelect({ label, value, onChange, options }: {
  label: string; value: string; onChange: (v: string) => void; options: { value: string; label: string }[];
}) {
  return (
    <div>
      <label className="text-[10px] font-normal text-slate-400 uppercase">{label}</label>
      <SelectField value={value} onChange={e => onChange(e.target.value)}
        className="w-full h-7 px-1.5 border border-slate-200 rounded text-xs focus:outline-none focus:ring-1 focus:ring-indigo-400 bg-white">
        <option value="">All</option>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </SelectField>
    </div>
  );
}

// ── Move Drawer ──────────────────────────────────────────────────────────────

function MoveDrawer({ selectedIds, leads, users, onClose, onMoved }: {
  selectedIds: string[]; leads: any[]; users: { id: string; name: string }[];
  onClose: () => void; onMoved: () => void;
}) {
  const [targetUserId, setTargetUserId] = useState('');
  const [saving, setSaving] = useState(false);
  const selectedLeads = leads.filter(l => selectedIds.includes(l.id));

  const handleMove = async () => {
    if (!targetUserId) { toast('error', 'Select a user'); return; }
    setSaving(true);
    try {
      const { data } = await api.post('/leads/bulk/move', { leadIds: selectedIds, assignedToId: targetUserId });
      toast('success', data.message);
      onMoved();
    } catch { toast('error', 'Failed to move leads'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="w-full max-w-md bg-white shadow-2xl flex flex-col h-full">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="text-base font-bold text-slate-900">Move Leads</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg">
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          <div>
            <p className="text-sm font-medium text-slate-700 mb-2">Selected Leads ({selectedLeads.length})</p>
            <div className="space-y-1.5">
              {selectedLeads.map(l => (
                <div key={l.id} className="flex items-center gap-2 px-3 py-2 bg-slate-50 rounded-lg">
                  <span className="text-sm font-medium text-slate-800">{l.name}</span>
                  <span className="text-xs text-slate-400">→ {l.assignedTo?.name || 'Unassigned'}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <label className="text-sm font-normal text-slate-700">Move to *</label>
            <SelectField value={targetUserId} onChange={e => setTargetUserId(e.target.value)}
              className="mt-1.5 w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400">
              <option value="">Select user</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
            </SelectField>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex gap-3 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
          <button onClick={handleMove} disabled={saving || !targetUserId}
            className="px-5 py-2 text-sm font-semibold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50">
            {saving ? 'Moving...' : `Move ${selectedLeads.length} Lead(s)`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Merge Drawer ─────────────────────────────────────────────────────────────

// Geo lookups now come from our own DB master (lib/geoApi), same as the
// lead form. Country list + cascading states/cities all from /api/geo/*.
const mergeFetchStates = getStateNames;
const mergeFetchCities = getCityNames;

const PRIORITY_OPTIONS = ['High', 'Medium', 'Low'];

// Scalar fields shown as editable inputs in the merge dialog. The user can
// type a final value freely or click a chip to pull either lead's value.
// `dd` marks fields rendered as searchable dropdowns.
const EDITABLE_FIELDS: { key: string; label: string; textarea?: boolean; dd?: 'status' | 'source' | 'country' | 'state' | 'city' | 'priority' }[] = [
  { key: 'name', label: 'Name' },
  { key: 'firstName', label: 'First Name' },
  { key: 'lastName', label: 'Last Name' },
  { key: 'status', label: 'Status', dd: 'status' },
  { key: 'source', label: 'Source', dd: 'source' },
  { key: 'priority', label: 'Priority', dd: 'priority' },
  { key: 'address', label: 'Address' },
  { key: 'country', label: 'Country', dd: 'country' },
  { key: 'state', label: 'State', dd: 'state' },
  { key: 'city', label: 'City', dd: 'city' },
  { key: 'pincode', label: 'Zipcode' },
  { key: 'campaignName', label: 'Campaign' },
  { key: 'notes', label: 'Notes', textarea: true },
];
function MergeDrawer({ leadIds, leads, onClose, onMerged }: {
  leadIds: string[]; leads: any[]; onClose: () => void; onMerged: () => void;
}) {
  const selectedLeads = leadIds.map(id => leads.find(l => l.id === id)).filter(Boolean);
  const [primaryId, setPrimaryId] = useState(leadIds[0]);
  const [values, setValues] = useState<Record<string, string>>({});
  const [primaryPhone, setPrimaryPhone] = useState('');
  const [primaryEmail, setPrimaryEmail] = useState('');
  const [statusOpts, setStatusOpts] = useState<string[]>([]);
  const [sourceOpts, setSourceOpts] = useState<string[]>([]);
  const [countryNames, setCountryNames] = useState<string[]>([]);
  const [geoStates, setGeoStates] = useState<string[]>([]);
  const [geoCities, setGeoCities] = useState<string[]>([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [saving, setSaving] = useState(false);

  const primary = selectedLeads.find(l => l.id === primaryId);
  const secondaryCount = selectedLeads.length - 1;

  const fieldStr = (lead: any, key: string): string => {
    const v = lead?.[key];
    return v == null ? '' : String(v);
  };

  // Distinct phones / emails across all selected leads (value + source lead).
  const collectList = (key: 'phones' | 'emails', valueKey: 'phone' | 'email') => {
    const seen = new Set<string>();
    const out: { value: string; from: string }[] = [];
    for (const l of selectedLeads) {
      const arr = Array.isArray(l?.[key]) ? l[key] : [];
      for (const item of arr) {
        const v = item?.[valueKey];
        if (v && !seen.has(v)) { seen.add(v); out.push({ value: v, from: l.name }); }
      }
    }
    return out;
  };
  const phoneList = collectList('phones', 'phone');
  const emailList = collectList('emails', 'email');

  // Load master status/source options for the searchable dropdowns.
  useEffect(() => {
    Promise.all([
      api.get('/leads/statuses').then(r => r.data).catch(() => []),
      api.get('/leads/sources').then(r => r.data).catch(() => []),
    ]).then(([st, sr]) => {
      setStatusOpts((Array.isArray(st) ? st : []).map((s: any) => s?.name ?? s).filter(Boolean));
      setSourceOpts((Array.isArray(sr) ? sr : []).map((s: any) => s?.name ?? s).filter(Boolean));
    });
    getCountryNames().then(setCountryNames).catch(() => {});
  }, []);

  // Re-seed editable values + primary phone/email from the primary lead.
  useEffect(() => {
    const init: Record<string, string> = {};
    for (const f of EDITABLE_FIELDS) init[f.key] = fieldStr(primary, f.key);
    setValues(init);
    const ph = formatLeadPhone((primary?.phones || []).find((p: any) => p.isPrimary)) || formatLeadPhone((primary?.phones || [])[0]) || '';
    const em = (primary?.emails || []).find((e: any) => e.isPrimary)?.email || (primary?.emails || [])[0]?.email || '';
    setPrimaryPhone(ph);
    setPrimaryEmail(em);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primaryId]);

  // Cascading geo: load states when the country is a known one; load cities
  // when both country and state are set. Guarded so partial typing in the
  // datalist doesn't fire a request on every keystroke.
  const country = values.country ?? '';
  const stateVal = values.state ?? '';
  useEffect(() => {
    if (country && countryNames.includes(country)) {
      let live = true;
      setLoadingStates(true);
      mergeFetchStates(country).then(s => { if (live) { setGeoStates(s); setLoadingStates(false); } });
      return () => { live = false; };
    }
    setGeoStates([]);
  }, [country, countryNames]);
  useEffect(() => {
    if (country && stateVal) {
      let live = true;
      setLoadingCities(true);
      mergeFetchCities(country, stateVal).then(c => { if (live) { setGeoCities(c); setLoadingCities(false); } });
      return () => { live = false; };
    }
    setGeoCities([]);
  }, [country, stateVal]);

  const ddOptions = (dd?: string): string[] | null => {
    switch (dd) {
      case 'status': return statusOpts;
      case 'source': return sourceOpts;
      case 'priority': return PRIORITY_OPTIONS;
      case 'country': return countryNames;
      case 'state': return geoStates;
      case 'city': return geoCities;
      default: return null;
    }
  };

  const setVal = (k: string, v: string) => setValues(prev => ({ ...prev, [k]: v }));

  const handleMerge = async () => {
    setSaving(true);
    try {
      const { data } = await api.post('/leads/bulk/merge', {
        primaryLeadId: primaryId,
        secondaryLeadIds: leadIds.filter(id => id !== primaryId),
        fieldsFromSecondary: ['phones', 'emails'],
        fieldOverrides: {
          ...values,
          countryId: countryIdByName(values.country) ?? undefined,
          stateId: stateIdByName(values.country, values.state) ?? undefined,
          cityId: cityIdByName(values.country, values.state, values.city) ?? undefined,
        },
        primaryPhone: primaryPhone || undefined,
        primaryEmail: primaryEmail || undefined,
      });
      toast('success', data.message);
      onMerged();
    } catch { toast('error', 'Failed to merge leads'); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/30" onClick={onClose} />
      <div className="w-full max-w-3xl bg-white shadow-2xl flex flex-col h-full">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <h2 className="text-base font-bold text-slate-900">Merge {selectedLeads.length} Leads</h2>
          <button onClick={onClose} className="p-1.5 hover:bg-slate-100 rounded-lg">
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">
          {/* Select primary */}
          <div>
            <label className="text-sm font-normal text-indigo-600 mb-2 block">Keep (Primary Lead)</label>
            <p className="text-xs text-slate-400 mb-2">The surviving record. The other {secondaryCount} lead(s) are merged in and removed.</p>
            <div className="grid grid-cols-2 gap-2">
              {selectedLeads.map(l => (
                <label key={l.id} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                  primaryId === l.id ? 'border-indigo-400 bg-indigo-50' : 'border-slate-200 hover:bg-slate-50'
                }`}>
                  <input type="radio" name="primary" checked={primaryId === l.id}
                    onChange={() => setPrimaryId(l.id)} className="accent-indigo-600" />
                  <div>
                    <p className="text-sm font-semibold text-slate-800">{l.name}</p>
                    <p className="text-xs text-slate-400">{l.status} · {l.source || '—'}</p>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Editable final values — two-column grid to reduce scrolling */}
          <div>
            <label className="text-sm font-normal text-indigo-600 mb-1 block">Final values</label>
            <p className="text-xs text-slate-400 mb-3">Edit any field. Click a chip to pull a value from one of the leads.</p>
            <div className="grid grid-cols-2 gap-x-4 gap-y-3">
              {EDITABLE_FIELDS.map(f => {
                const distinct = Array.from(new Set(selectedLeads.map(l => fieldStr(l, f.key)).filter(Boolean)));
                const opts = ddOptions(f.dd);
                const inputCls = 'w-full text-sm border border-slate-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500';
                return (
                  <div key={f.key} className={f.textarea ? 'col-span-2' : ''}>
                    <label className="text-xs font-normal text-slate-600 mb-1 block">{f.label}</label>
                    {opts ? (
                      <SearchableSelect
                        options={opts}
                        value={values[f.key] ?? ''}
                        onChange={v => setVal(f.key, v)}
                        placeholder={`Select ${f.label.toLowerCase()}…`}
                        loading={f.dd === 'state' ? loadingStates : f.dd === 'city' ? loadingCities : false}
                      />
                    ) : f.textarea ? (
                      <textarea value={values[f.key] ?? ''} onChange={e => setVal(f.key, e.target.value)} rows={2} className={inputCls} />
                    ) : (
                      <input value={values[f.key] ?? ''} onChange={e => setVal(f.key, e.target.value)} className={inputCls} />
                    )}
                    {distinct.length > 1 && (
                      <div className="flex gap-1 flex-wrap mt-1.5">
                        {distinct.map(val => (
                          <button key={val} type="button" onClick={() => setVal(f.key, val)}
                            className={`px-1.5 py-0.5 text-[10px] rounded max-w-[130px] truncate ${
                              values[f.key] === val ? 'bg-indigo-100 text-indigo-700' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'
                            }`}
                            title={val}>{val}</button>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Phones — all merged, pick the primary */}
          <div>
            <label className="text-sm font-normal text-indigo-600 mb-1 block">Phone numbers</label>
            <p className="text-xs text-slate-400 mb-2">All phones are merged (de-duplicated). Select the primary.</p>
            <div className="space-y-1">
              {phoneList.length === 0 && <p className="text-xs text-slate-400">No phone numbers</p>}
              {phoneList.map(p => (
                <label key={p.value} className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                  primaryPhone === p.value ? 'bg-indigo-50 border border-indigo-200' : 'hover:bg-slate-50 border border-transparent'
                }`}>
                  <input type="radio" name="primaryPhone" checked={primaryPhone === p.value}
                    onChange={() => setPrimaryPhone(p.value)} className="accent-indigo-600" />
                  <span className="text-sm text-slate-700 flex-1">{p.value}</span>
                  <span className="text-[11px] text-slate-400">from {p.from}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Emails — all merged, pick the primary */}
          <div>
            <label className="text-sm font-normal text-indigo-600 mb-1 block">Email addresses</label>
            <p className="text-xs text-slate-400 mb-2">All emails are merged (de-duplicated). Select the primary.</p>
            <div className="space-y-1">
              {emailList.length === 0 && <p className="text-xs text-slate-400">No email addresses</p>}
              {emailList.map(em => (
                <label key={em.value} className={`flex items-center gap-3 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                  primaryEmail === em.value ? 'bg-indigo-50 border border-indigo-200' : 'hover:bg-slate-50 border border-transparent'
                }`}>
                  <input type="radio" name="primaryEmail" checked={primaryEmail === em.value}
                    onChange={() => setPrimaryEmail(em.value)} className="accent-indigo-600" />
                  <span className="text-sm text-slate-700 flex-1 truncate">{em.value}</span>
                  <span className="text-[11px] text-slate-400">from {em.from}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
        <div className="px-6 py-4 border-t border-slate-100 flex gap-3 justify-end">
          <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancel</button>
          <button onClick={handleMerge} disabled={saving}
            className="px-5 py-2 text-sm font-semibold bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50">
            {saving ? 'Merging...' : `Merge ${secondaryCount} into primary`}
          </button>
        </div>
      </div>
    </div>
  );
}
